import { imgUrl } from '@/request/http'
import { getWebsiteHome } from '@/request/apis/website'
import { normalizeWebsiteScale, normalizeThemeColor, getContrastingTextColor } from '@/request/apis/websiteCatalog'

const homeCache = {}

function normalizeLang(lang) {
  return lang === 'en' ? 'en' : 'zh'
}

function pickByLang(zhValue, enValue, lang) {
  return lang === 'en' ? (enValue || zhValue || '') : (zhValue || enValue || '')
}

function formatTime(beginTime, endTime) {
  if (beginTime && endTime) return `${beginTime} ~ ${endTime}`
  if (beginTime) return beginTime
  return ''
}

function compactLocation(value, lang) {
  if (!value) return ''
  if (lang === 'en') {
    return String(value).split(/[,\s]/).filter(Boolean).slice(0, 2).join(' ')
  }
  const text = String(value)
  const matched = text.match(/北京|上海|天津|重庆|广州|深圳|南京|南宁|西安|温州|郑州|珠海|杭州|成都|武汉|长沙|苏州|厦门|青岛|宁波|佛山|东莞|合肥|福州|济南|昆明/)
  if (matched) return matched[0]
  const city = text.match(/[\u4e00-\u9fa5]{2,4}市?/)
  if (city) return city[0].replace(/市$/, '')
  return text.replace(/\s+/g, '').slice(0, 4)
}

function normalizeImage(path) {
  if (!path) return ''
  const value = String(path).trim()
  if (!value) return ''
  if (/^(https?:)?\/\//.test(value) || /^data:/.test(value)) return value

  const profilePath = value
    .replace(/^\/+/, '')
    .replace(/^profile\/?/, '')

  return `${imgUrl.replace(/\/+$/, '')}/${profilePath}`
}

function normalizeScaleKey(value) {
  return String(value || '')
    .replace(/\s+/g, '')
    .replace(/（/g, '(')
    .replace(/）/g, ')')
    .replace(/中文版/g, 'CN')
    .toUpperCase()
}

function stripScaleCodeFromTitle(value, code) {
  const text = String(value || '').replace(/\s+/g, ' ').trim()
  const normalizedCode = String(code || '').trim()
  if (!text || !normalizedCode) return text

  const codeVariants = [
    normalizedCode,
    normalizedCode.replace(/\(CN\)/ig, '(中文版)'),
    normalizedCode.replace(/\(CN\)/ig, '（中文版）')
  ].filter(Boolean)

  return codeVariants.reduce((result, variant) => {
    const escaped = variant.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return result.replace(new RegExp(`\\s*${escaped}\\s*`, 'ig'), ' ')
  }, text).replace(/\s+/g, ' ').trim()
}

function addTrainingScaleLookupEntry(lookup, key, info) {
  const normalizedKey = normalizeScaleKey(key)
  if (!normalizedKey || lookup[normalizedKey]) return
  lookup[normalizedKey] = info
}

function buildTrainingScaleLookup(scales, lang) {
  const lookup = {}
  ;(scales || []).forEach((item) => {
    const normalized = normalizeWebsiteScale(item, lang)
    const code = normalized.code || normalized.shortName || normalized.name || ''
    const summarySource = normalized.summary || normalized.name || normalized.shortName || ''
    const info = {
      code,
      summary: stripScaleCodeFromTitle(summarySource, code),
      ribbonColor: normalized.ribbonColor,
      ribbonTextColor: normalized.ribbonTextColor
    }

    ;[
      normalized.code,
      normalized.shortName,
      normalized.name,
      normalized.slug,
      normalized.key,
      normalized.sourceId,
      normalized.detailTemplateKey
    ].forEach(key => addTrainingScaleLookupEntry(lookup, key, info))
  })
  return lookup
}

function addScaleRibbonLookupEntry(lookup, key, theme) {
  const normalizedKey = normalizeScaleKey(key)
  if (!normalizedKey || !theme || !theme.ribbonColor || lookup[normalizedKey]) return
  lookup[normalizedKey] = theme
}

function buildScaleRibbonLookup(scales, lang) {
  const lookup = {}
  ;(scales || []).forEach((item) => {
    const normalized = normalizeWebsiteScale(item, lang)
    const theme = {
      ribbonColor: normalizeThemeColor(normalized.ribbonColor) || '#0B8D93',
      ribbonTextColor: getContrastingTextColor(normalized.ribbonColor)
    }
    ;[
      item && item.id,
      normalized.id,
      normalized.sourceId,
      normalized.key,
      normalized.slug,
      normalized.code,
      normalized.shortName,
      normalized.name
    ].forEach(key => addScaleRibbonLookupEntry(lookup, key, theme))
  })
  return lookup
}

function resolveTrainingScaleInfo(scaleCode, lookup, directThemeColor) {
  const fallbackCode = String(scaleCode || '').trim()
  const resolved = lookup[normalizeScaleKey(fallbackCode)]
  const ribbonColor = normalizeThemeColor(directThemeColor) ||
    normalizeThemeColor(resolved && resolved.ribbonColor) ||
    '#0B8D93'
  return {
    code: resolved && resolved.code ? resolved.code : fallbackCode,
    summary: resolved && resolved.summary ? resolved.summary : '',
    ribbonColor,
    ribbonTextColor: getContrastingTextColor(ribbonColor)
  }
}

function firstCourse(session) {
  const courses = session && Array.isArray(session.courses) ? session.courses : []
  return courses[0] || {}
}

function collectScaleThemes(session, lookup) {
  const courses = session && Array.isArray(session.courses) ? session.courses : []
  const themes = []
  const seenScaleKeys = {}
  courses.forEach((course) => {
    const scaleCodes = Array.isArray(course.scaleCodes) ? course.scaleCodes : []
    const scaleThemeColors = Array.isArray(course.scaleThemeColors) ? course.scaleThemeColors : []
    scaleCodes.forEach((code, index) => {
      const normalizedCode = normalizeScaleKey(code)
      if (normalizedCode && !seenScaleKeys[normalizedCode]) {
        seenScaleKeys[normalizedCode] = true
        themes.push(resolveTrainingScaleInfo(code, lookup, scaleThemeColors[index]))
      }
    })
  })
  return themes.slice(0, 2)
}

export function normalizeSiteConfig(config) {
  const value = config || {}
  return {
    phone: value.phone || '',
    email: value.email || '',
    address: value.address || '',
    wechatQr: normalizeImage(value.wechatQr),
    wechatId: value.wechatId || '',
    newsBanner: normalizeImage(value.newsBanner),
    aboutBanner: normalizeImage(value.aboutBanner),
    socialResponsibilityBanner: normalizeImage(value.socialResponsibilityBanner),
    icp: value.icp || '',
    police: value.police || '',
    company: value.company || ''
  }
}

function normalizeHeroDescription(desc) {
  return String(desc || '')
    .replace('以中国本土常模为基础', '我们建立中国本土常模数据')
}

function mapHero(hero) {
  const value = hero || {}
  return {
    title: value.title || '',
    desc: normalizeHeroDescription(value.description || value.desc || ''),
    image: normalizeImage(value.image),
    stat: value.stat || null
  }
}

function mapTrainingSessionList(rows, lang, trainingScaleLookup = {}) {
  return (rows || []).map((session) => {
    const course = firstCourse(session)
    const scaleThemes = collectScaleThemes(session, trainingScaleLookup)
    const scaleCodes = scaleThemes.map(item => item.code)
    const title = pickByLang(course.title, course.titleEn || course.englishTitle, lang) ||
      pickByLang(session.sessionName, session.sessionNameEn, lang)
    return {
      id: session.sessionId,
      sessionId: session.sessionId,
      title,
      shortTitle: scaleCodes.length ? scaleCodes.slice(0, 2).join(' / ') : title,
      summary: scaleThemes.length ? scaleThemes[0].summary : '',
      time: formatTime(course.beginTime, course.endTime),
      location: compactLocation(pickByLang(session.location, session.locationEn, lang), lang),
      status: session.displayStatus || '',
      scaleCode: scaleCodes[0] || '',
      scaleCodes,
      scaleSummaries: scaleThemes,
      scaleThemes,
      cover: normalizeImage(course.coverImage || course.poster)
    }
  })
}

function resolveExpertRibbonColor(item, lookup) {
  const directColor = normalizeThemeColor(item && item.primaryScaleThemeColor)
  if (directColor) {
    return {
      ribbonColor: directColor,
      ribbonTextColor: getContrastingTextColor(directColor)
    }
  }
  const keys = [
    item && item.primaryScaleId,
    item && item.primaryScaleCode,
    item && item.primaryScaleName,
    item && item.primaryScale
  ]
  for (let index = 0; index < keys.length; index += 1) {
    const theme = lookup[normalizeScaleKey(keys[index])]
    if (theme) return theme
  }
  return {
    ribbonColor: '#0B8D93',
    ribbonTextColor: getContrastingTextColor('#0B8D93')
  }
}

function mapExpertList(rows, lang, scaleRibbonLookup = {}) {
  return (rows || []).map((item) => {
    const primaryScale = item.primaryScaleCode || item.primaryScaleName || ''
    const theme = resolveExpertRibbonColor(item, scaleRibbonLookup)
    return {
      id: item.id,
      name: pickByLang(item.memberName, item.memberNameEn, lang),
      memberName: item.memberName,
      memberNameEn: item.memberNameEn,
      institution: item.institution,
      institutionEn: item.institutionEn,
      post: pickByLang(item.post || item.institution, item.postEn || item.institutionEn, lang),
      summary: '',
      avatar: normalizeImage(item.avatar),
      primaryScale,
      primaryScaleId: item.primaryScaleId,
      primaryScaleCode: item.primaryScaleCode,
      primaryScaleThemeColor: item.primaryScaleThemeColor,
      ribbonColor: theme.ribbonColor,
      ribbonTextColor: theme.ribbonTextColor,
      scaleIds: item.scaleIds || [],
      scales: item.scaleIds || []
    }
  })
}

function mapNewsList(rows, lang) {
  return (rows || []).map((item) => {
    return {
      id: item.id,
      title: pickByLang(item.title, item.titleEn, lang),
      summary: pickByLang(item.summary, item.summaryEn, lang),
      location: pickByLang(item.location, item.locationEn, lang),
      cover: normalizeImage(item.coverPhoto),
      date: item.newsDate || '',
      type: item.newsType || '1'
    }
  })
}

function mapScaleCatalogForHome(rows, lang) {
  return (rows || []).map((item) => {
    const normalized = normalizeWebsiteScale(item, lang)
    const summary = String(normalized.summary || '').trim() ||
      String(normalized.name || '').trim() ||
      String(normalized.shortName || '').trim() ||
      String(normalized.code || '').trim()
    return Object.assign({}, normalized, {
      title: normalized.shortName || normalized.name || normalized.code,
      summary,
      primaryScale: normalized.code,
      scales: normalized.code ? [normalized.code] : []
    })
  })
}

function emptyHomeData() {
  return {
    heroConfig: {},
    siteConfig: {},
    productCarousel: [],
    trainingList: [],
    expertList: [],
    newsList: []
  }
}

export function normalizeHomeAggregate(data, lang = 'zh') {
  const value = data || {}
  const scales = value.scales || []
  const trainingScaleLookup = buildTrainingScaleLookup(scales, lang)
  const scaleRibbonLookup = buildScaleRibbonLookup(scales, lang)
  return {
    heroConfig: mapHero(value.hero),
    siteConfig: normalizeSiteConfig(value.siteConfig),
    productCarousel: mapScaleCatalogForHome(value.scales || [], lang),
    trainingList: mapTrainingSessionList(value.trainings || [], lang, trainingScaleLookup),
    expertList: mapExpertList(value.experts || [], lang, scaleRibbonLookup),
    newsList: mapNewsList(value.news || [], lang)
  }
}

function loadHomeAggregate(lang) {
  if (!homeCache[lang]) {
    homeCache[lang] = getWebsiteHome(lang)
      .then(res => normalizeHomeAggregate(res.data, lang))
      .catch(error => {
        delete homeCache[lang]
        throw error
      })
  }
  return homeCache[lang]
}

export function getHomePageData(lang = 'zh') {
  return loadHomeAggregate(normalizeLang(lang))
}

export function getHomeSiteConfig(lang = 'zh') {
  return getHomePageData(lang).then(data => data.siteConfig || {})
}

export function getEmptyHomePageData() {
  return emptyHomeData()
}

export function clearHomePageDataCache() {
  Object.keys(homeCache).forEach(key => {
    delete homeCache[key]
  })
}



// WEBPACK FOOTER //
// ./src/request/apis/home.js